import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RefreshCw, Loader2, Sparkles, Clock, Check } from 'lucide-react';

import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import AgentSettingsModal from './AgentSettingsModal';
import BuyCreditsModal from '../../../components/Modals/BuyCreditsModal';
import DigestHistory from './DigestHistory';
import DigestQueue from './DigestQueue';
import DigestDetailPane from './DigestDetailPane';
import { DDStat, CreditBar, SettingsStrip } from './DigestSummary';
import { parseDigestDate, isTerminal } from './digestHelpers';
import {
    fetchTodayDigest, regenerateDigest, updateAction,
    fetchAgentSettings, DIGEST_ACTION_STATUSES,
} from '../../../redux/DigestSlice/digestSlice';
import './digest-design.css';

/**
 * Daily Digest — rebuilt against the Career Champ design project.
 *
 * TODAY is a triage queue: a dense scannable list on the left, one detail pane
 * on the right. The previous card grid rendered every one of 40–100 proposals
 * at full height, which made the day impossible to survey and put the price of
 * the whole run below the fold.
 *
 * HISTORY is a timeline of runs. See DigestHistory.jsx for what the summarised
 * endpoint can and cannot show.
 */

const formatDigestDate = (digestDate) => {
    const d = parseDigestDate(digestDate);
    return d
        ? d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
        : String(digestDate || '');
};

const DailyDigestPage = () => {
    const dispatch = useDispatch();
    const {
        digest, loading, creditsBalance, settings, channels,
    } = useSelector((state) => state.digest);

    const [tab, setTab] = useState('today');
    const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [checkedIds, setCheckedIds] = useState(() => new Set());
    const [selectedId, setSelectedId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchTodayDigest());
        // Settings and channels drive the strip and the credit bar, so they are
        // fetched by the page rather than only by the (collapsed) settings panel.
        dispatch(fetchAgentSettings());
    }, [dispatch]);

    const actions = useMemo(() => digest?.actions || [], [digest]);
    const counts = digest?.counts || { proposed: 0, sent: 0, replies: 0 };

    // A rebuilt digest invalidates any selection — those actionIds may be gone.
    useEffect(() => { setCheckedIds(new Set()); setSelectedId(null); }, [digest?._id]);

    const sourceCounts = useMemo(() => {
        const tally = { all: actions.length };
        for (const a of actions) {
            const key = a.source?.type || 'other';
            tally[key] = (tally[key] || 0) + 1;
        }
        return tally;
    }, [actions]);

    /**
     * 'all' means all OPPORTUNITIES — signals and jobs — not literally every
     * row. Contacts are a separate group with a different verb and a different
     * cost, so mixing them back in under "All" would undo the distinction the
     * two filter groups exist to make.
     */
    const visibleActions = useMemo(() => {
        if (sourceFilter === 'all') return actions.filter((a) => a.source?.type !== 'contact');
        return actions.filter((a) => a.source?.type === sourceFilter);
    }, [actions, sourceFilter]);

    // Keep a row selected at all times so the detail pane is never empty while
    // the queue has something in it.
    useEffect(() => {
        if (visibleActions.length === 0) { setSelectedId(null); return; }
        if (!visibleActions.some((a) => a.actionId === selectedId)) {
            setSelectedId(visibleActions[0].actionId);
        }
    }, [visibleActions, selectedId]);

    const selectedAction = useMemo(
        () => actions.find((a) => a.actionId === selectedId) || null,
        [actions, selectedId]
    );
    const selectedIndex = useMemo(
        () => visibleActions.findIndex((a) => a.actionId === selectedId),
        [visibleActions, selectedId]
    );

    const toggleCheck = useCallback((actionId) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(actionId)) next.delete(actionId); else next.add(actionId);
            return next;
        });
    }, []);

    const toggleAllVisible = useCallback((ids, allChecked) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) ids.forEach((id) => next.delete(id));
            else ids.forEach((id) => next.add(id));
            return next;
        });
    }, []);

    const checkedActions = useMemo(
        // Message rows have no bulk action, so a selection made on the Contacts
        // tab before switching tabs can never surface them in the bar.
        () => actions.filter((a) => checkedIds.has(a.actionId) && a.kind === 'fetch_contact'),
        [actions, checkedIds]
    );
    const checkedCredits = useMemo(
        () => checkedActions.reduce((sum, a) => sum + (a.creditCost || 0), 0),
        [checkedActions]
    );

    /**
     * Only rows EXECUTE WILL ACT ON count as approved.
     *
     * Approving is a spend authorisation, and the executor spends on
     * fetch_contact rows alone — it skips every email / linkedin_* action. So an
     * approved message row set a status nothing downstream ever read: it looked
     * like a commitment and did nothing. Counting those here also made the stat
     * card disagree with the Execute button, which has always filtered to fetch
     * rows: the card said "Approved 2 · 20 credits" beside a button reading
     * "Execute 1 approved · 20 credits".
     */
    const approvedActions = useMemo(
        () => actions.filter((a) => a.status === 'approved' && a.kind === 'fetch_contact'),
        [actions]
    );
    const approvedCredits = useMemo(
        () => approvedActions.reduce((sum, a) => sum + (a.creditCost || 0), 0),
        [approvedActions]
    );

    // Regenerate and Execute now live in the top bar for this route — see
    // DigestNavActions, rendered by Navbaar on /daily-digest. Execute spends
    // credits, so it belongs somewhere fixed rather than above a long queue.

    const runBulk = async (status) => {
        if (!digest || checkedActions.length === 0 || bulkBusy) return;
        setBulkBusy(true);
        /**
         * Approving is only meaningful on a row that costs money to execute.
         * "Select all visible" then Approve used to mark contact rows too,
         * creating approvals that could never do anything — which is exactly how
         * a contact ends up sitting in the list labelled "Approved" forever.
         * Skip stays available on everything: skipping a contact is a real
         * decision the exclusion set reads.
         */
        const targets = checkedActions
            .filter((a) => !isTerminal(a))
            .filter((a) => status !== DIGEST_ACTION_STATUSES.APPROVED || a.kind === 'fetch_contact');
        if (targets.length === 0) { setBulkBusy(false); setCheckedIds(new Set()); return; }
        await Promise.all(
            targets.map((a) => dispatch(updateAction({ digestId: digest._id, actionId: a.actionId, status })))
        );
        setBulkBusy(false);
        setCheckedIds(new Set());
    };

    const generatedAt = digest?.createdAt
        ? new Date(digest.createdAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
        : null;

    const renderToday = () => {
        if (loading && !digest) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--cc-ink-400)' }}>
                    <Loader2 size={22} className="animate-spin" />
                </div>
            );
        }

        if (!digest || actions.length === 0) {
            /* Never render a blank page — silence is indistinguishable from a
               broken feature. */
            return (
                <div className="cc-card" style={{ padding: '64px 24px', textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg,#3F57E0,#7C3AED)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                        <Sparkles size={26} color="#fff" />
                    </div>
                    <p className="cc-h3">
                        {digest ? 'The agent ran and found nothing today' : 'No digest yet'}
                    </p>
                    <p className="cc-tiny" style={{ marginTop: 6, maxWidth: 380, marginInline: 'auto' }}>
                        {digest
                            ? 'No new outreach was proposed for today. Check back tomorrow, or regenerate now.'
                            : 'We couldn’t load today’s digest. Try regenerating it.'}
                    </p>
                    <button
                        type="button"
                        className="cc-btn cc-btn-primary"
                        style={{ marginTop: 18 }}
                        onClick={() => dispatch(regenerateDigest())}
                        disabled={loading}
                    >
                        <RefreshCw style={{ width: 13, height: 13 }} />Regenerate
                    </button>
                </div>
            );
        }

        return (
            <>
                <SettingsStrip
                    settings={settings}
                    channels={channels}
                    onOpenSettings={() => setSettingsOpen(true)}
                />

                <CreditBar
                    actions={actions}
                    approvedActions={approvedActions}
                    approvedCredits={approvedCredits}
                    creditsBalance={creditsBalance}
                    /* The same modal the navbar's Buy Credits opens. It used to
                       set window.location.href, which is a full page load: the
                       user lost the digest they were triaging and came back to a
                       cold app just to top up. */
                    onBuyCredits={() => setBuyCreditsOpen(true)}
                />

                <div className="cc-digest-split">
                    <DigestQueue
                        actions={visibleActions}
                        sourceCounts={sourceCounts}
                        sourceFilter={sourceFilter}
                        onSourceFilter={setSourceFilter}
                        selectedId={selectedId}
                        onSelect={(a) => setSelectedId(a.actionId)}
                        checkedIds={checkedIds}
                        onToggleCheck={toggleCheck}
                        onToggleAllVisible={toggleAllVisible}
                    />

                    <div className="cc-digest-detail" style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
                        {checkedActions.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderRadius: 12, background: 'var(--cc-primary-50)', border: '1px solid var(--cc-primary-100)', flexWrap: 'wrap' }}>
                                <Check style={{ width: 14, height: 14, color: 'var(--cc-primary)' }} />
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cc-primary-700)' }}>
                                    {checkedActions.length} selected
                                </span>
                                {checkedCredits > 0 && <span className="cc-tiny">· {checkedCredits} credits</span>}
                                <span style={{ flex: 1 }} />
                                <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" disabled={bulkBusy} onClick={() => runBulk(DIGEST_ACTION_STATUSES.SKIPPED)}>
                                    Skip selected
                                </button>
                                <button type="button" className="cc-btn cc-btn-primary cc-btn-sm" disabled={bulkBusy} onClick={() => runBulk(DIGEST_ACTION_STATUSES.APPROVED)}>
                                    {bulkBusy ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Check style={{ width: 12, height: 12 }} />}
                                    Approve selected
                                </button>
                            </div>
                        )}

                        <DigestDetailPane
                            digestId={digest._id}
                            action={selectedAction}
                            position={selectedIndex >= 0 ? selectedIndex + 1 : 0}
                            total={visibleActions.length}
                            senderEmail={channels?.google?.email}
                        />

                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="relative h-screen bg-[#F6F7FB] text-slate-900 font-dmsans pt-16 flex flex-col overflow-hidden">
            <Navbaar />

            <div className="flex flex-1 min-h-0 overflow-hidden">
                <aside className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </aside>

                <main className="cc-scope flex-1 flex flex-col overflow-y-auto custom-scrollbar">
                    <div style={{ padding: '22px 28px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                            <div className="cc-grow" style={{ minWidth: 220 }}>
                                <div className="cc-h1">
                                    {tab === 'today' && digest ? formatDigestDate(digest.digestDate) : 'Daily Digest'}
                                </div>
                                <div className="cc-tiny" style={{ marginTop: 4 }}>
                                    {tab === 'today' && digest
                                        ? `${generatedAt ? `Generated ${generatedAt} · ` : ''}${actions.length} opportunit${actions.length === 1 ? 'y' : 'ies'} matched to your profile`
                                        : 'Your AI networking agent’s picks for today'}
                                </div>
                            </div>

                            {tab === 'today' && digest && (
                                <>
                                    <DDStat label="Proposed" value={actions.length} sub="ready to triage" />
                                    <DDStat
                                        label="Approved"
                                        value={approvedActions.length}
                                        tint="#047857"
                                        sub={`${approvedCredits} credit${approvedCredits === 1 ? '' : 's'}`}
                                    />
                                    <DDStat label="Sent" value={counts.sent} sub={counts.sent ? 'executed' : 'nothing executed yet'} />
                                    {/* Replies are not detected yet (counts.replies is never written),
                                        so a 0 here would read as "nobody answered" rather than
                                        "we cannot tell". */}
                                    <DDStat label="Replies" value="—" sub="not tracked yet" />
                                </>
                            )}
                        </div>

                        <div className="cc-tabs">
                            <button type="button" className={`cc-tab ${tab === 'today' ? 'is-active' : ''}`} onClick={() => setTab('today')}>
                                <Sparkles style={{ width: 13, height: 13 }} />Today
                            </button>
                            <button type="button" className={`cc-tab ${tab === 'history' ? 'is-active' : ''}`} onClick={() => setTab('history')}>
                                <Clock style={{ width: 13, height: 13 }} />History
                            </button>
                        </div>

                        {tab === 'today' ? renderToday() : <DigestHistory onGoToToday={() => setTab('today')} />}

                        {/* Mounted at page level, not inside renderToday: the modal
                            portals to document.body, and keeping it out of the tab
                            branch means switching tabs cannot strand it open. */}
                        <AgentSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
                        <BuyCreditsModal
                            isOpen={buyCreditsOpen}
                            onClose={() => setBuyCreditsOpen(false)}
                        />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DailyDigestPage;
